import { useEffect, useLayoutEffect, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { useAtmosphereSettings } from '../../hooks/useAtmosphereSettings';

/**
 * Round 14 (2026-06-06, Sprint 2 §4.3) — Animated chip dispatch for
 * generic Save / Damage / Skill Check rolls.
 *
 * Mirrors HeroPointChipPortal (A6) — flies a result chip from the
 * tumble overlay into a target element after the dice settle. Differs
 * in two ways:
 *
 *   1. SOURCE is a STATIC point captured by ScenarioRunner BEFORE the
 *      tumble unmounts (the chip element is ephemeral; HP's source is
 *      permanent). HeroPointChipPortal reads its source from a ref;
 *      we accept `sourcePoint: ScreenPoint` directly.
 *   2. TARGET is the History drawer toggle button — flying the chip
 *      to the toggle visually signals "this result is now in history."
 *
 * Per user choice 2026-06-06: percentile rolls SKIP this animation
 * (the QR cinematic card is already their visual reveal). ScenarioRunner
 * enforces that by not populating `pendingChipFly` for percentile-mode.
 *
 * Atmosphere gate: `diceChipFlyEnabled && animationsEnabled`. When
 * disabled, fires `onAnimationComplete()` immediately and returns null
 * (no chip rendered).
 */

interface ScreenPoint {
  x: number;
  y: number;
}

export interface DiceChipPortalProps {
  /** Static screen position the chip flies FROM. Captured by the
   *  parent before the tumble unmounts (chip element is ephemeral). */
  sourcePoint: ScreenPoint;
  /** Element the chip flies TO. Read via ref so the position is fresh
   *  at portal launch — the History toggle may shift if the viewport
   *  changes during the brief flight. */
  targetRef: RefObject<HTMLElement | null>;
  /** Result payload — for chip body content. */
  result: {
    mode: 'save' | 'damage' | 'skill_check' | 'percentile';
    total: number;
    breakdown: number[];
  };
  /** Dice notation, e.g. "1d20" or "2d6". */
  notation: string;
  /** Fired once the chip has landed at the target. */
  onAnimationComplete: () => void;
  /** Flight duration ms. Default 600ms — snappier than the HP chip's
   *  700ms since the dice chip distance is typically shorter. */
  durationMs?: number;
  /** Optional testid prefix; default 'br-dice-chip-portal'. */
  testidPrefix?: string;
}

const DEFAULT_DURATION_MS = 600;

function centerOfRef(ref: RefObject<HTMLElement | null>): ScreenPoint | null {
  const el = ref.current;
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 && rect.height <= 0) return null;
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}

function viewportCenter(): ScreenPoint {
  const w = typeof window === 'undefined' ? 1024 : window.innerWidth;
  const h = typeof window === 'undefined' ? 768 : window.innerHeight;
  return { x: w / 2, y: h / 2 };
}

export function DiceChipPortal({
  sourcePoint,
  targetRef,
  result,
  notation,
  onAnimationComplete,
  durationMs = DEFAULT_DURATION_MS,
  testidPrefix = 'br-dice-chip-portal',
}: DiceChipPortalProps) {
  const { diceChipFlyEnabled, animationsEnabled } = useAtmosphereSettings();
  const animationActive = diceChipFlyEnabled && animationsEnabled;

  useEffect(() => {
    if (animationActive) return;
    onAnimationComplete();
  }, [animationActive, onAnimationComplete]);

  const [target, setTarget] = useState<ScreenPoint | null>(null);

  useLayoutEffect(() => {
    setTarget(centerOfRef(targetRef) ?? viewportCenter());
  }, [targetRef]);

  if (!animationActive) return null;
  if (typeof document === 'undefined') return null;
  if (target === null) return null;

  const durationSeconds = durationMs / 1000;
  const { mode, total, breakdown } = result;
  const ariaLabel =
    mode === 'percentile' ? `Percentile roll: ${total} percent`
    : mode === 'damage' ? `Damage roll total: ${total}. Dice: ${breakdown.join(', ')}.`
    : mode === 'save' ? `Save roll: ${total} on ${notation}`
    : /* skill_check */ `Skill check: ${total}`;

  return createPortal(
    <div
      className="br-dice-chip-portal-root"
      data-testid={`${testidPrefix}-root`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        pointerEvents: 'none',
      }}
    >
      <motion.div
        data-testid={testidPrefix}
        data-roll-mode={mode}
        style={{ position: 'absolute', willChange: 'transform' }}
        initial={{
          x: sourcePoint.x,
          y: sourcePoint.y,
          scale: 1.0,
          opacity: 1,
        }}
        animate={{
          x: [sourcePoint.x, sourcePoint.x, target.x],
          y: [sourcePoint.y, sourcePoint.y, target.y],
          scale: [1.0, 1.15, 0.9],
          opacity: [1, 1, 0.85],
        }}
        transition={{
          duration: durationSeconds,
          times: [0, 0.3, 1],
          ease: 'easeOut',
        }}
        onAnimationComplete={onAnimationComplete}
      >
        <div
          className={`br-dice-roll-tumble-value br-dice-roll-tumble-value--${mode} br-dice-chip-portal-chip`}
          data-testid={`${testidPrefix}-chip`}
          aria-label={ariaLabel}
          style={{
            transform: 'translate(-50%, -50%)',
            position: 'relative',
          }}
        >
          <span className="br-dice-roll-tumble-value-number">{total}</span>
          {mode === 'percentile' && (
            <span className="br-dice-roll-tumble-value-suffix">%</span>
          )}
          {mode === 'damage' && breakdown.length > 1 && (
            <span className="br-dice-roll-tumble-value-meta">
              = {breakdown.join('+')}
            </span>
          )}
        </div>
      </motion.div>
    </div>,
    document.body,
  );
}

export default DiceChipPortal;
